import React from 'react';
import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';

import { Button } from './ui/button';
import { regionLabel } from '../lib/regions';
import { placeLabel, placeOf } from '../lib/holidayPlaces';
import { removeTitle } from '../lib/holidayActions';

/**
 * A company's public holidays, and the ones they've taken off their calendar.
 *
 * ⚠️ ONE IMPLEMENTATION, TWO SCREENS — Rob's option A, 13/08/2026, and his reasoning was
 * the right one: *"otherwise we are possibly leaving a trail of jobs for another day."*
 * The Holidays page and the signup wizard both show this list and both offer the same
 * actions, and a second copy would be the fourth thing this week that drifted quietly.
 *
 * ⚠️ IT OWNS NO DATA AND CALLS NO API. Fetching, refreshing and the confirm dialog stay
 * with the page, because the two screens legitimately differ there — signup has no
 * `fetchData` and no sidebar. What is shared is what an admin SEES and the words on it.
 *
 * `dense` is for signup, where this sits inside a wizard card rather than on a page of
 * its own: smaller rows, no icons, no card chrome. Same rows, same wording, less furniture.
 */
const HolidayDayList = ({
  holidays = [],
  optedOut = [],
  canEdit = false,
  onRemove,
  onRestore,
  dense = false,
  emptyMessage = 'No public holidays configured',
  countryNames = {},
  multiCountry = false,
}) => {
  if (!holidays.length && !optedOut.length) {
    return (
      <div className={dense ? 'py-4 text-center' : 'p-8 text-center'}>
        {!dense && <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground mb-4" />}
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  const Row = ({ holiday, action, muted }) => (
    <div
      className={`${dense ? 'py-2' : 'p-4'} flex items-center justify-between gap-3 ${
        dense ? '' : 'hover:bg-secondary/30 transition-colors'}`}
      data-testid={`holiday-${holiday.id}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        {!dense && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            muted ? 'bg-secondary' : 'bg-primary/20'}`}>
            <CalendarDays className={`w-5 h-5 ${muted ? 'text-muted-foreground' : 'text-primary'}`} />
          </div>
        )}
        <div className="min-w-0">
          <p className={dense ? 'text-sm font-medium' : 'font-medium'}>{holiday.name}</p>
          <p className="text-xs text-muted-foreground">
            {/* The long form is deliberate — the WEEKDAY is the thing an admin is
                checking. "Mon 25/12/2026" tells you more than "25/12/2026" when the
                question is whether this lands on a working day. */}
            {format(new Date(holiday.date), dense ? 'EEE d MMM yyyy' : 'EEEE, d MMMM yyyy')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* ⚠️ WHICH REGION. Without it the list looks broken on any multi-region customer:
            England & Wales, Scotland and Northern Ireland each have their own 1 January
            row, so the same name appears three times with nothing to tell them apart. */}
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {/* ⚠️ A COMPANY DAY IS DESCRIBED BY ITS PLACE, NOT ITS REGION. Since 13/08/2026
              an admin can add one for everyone or for one country, and "All regions" —
              which means every region of ONE country — would be the wrong words for a day
              that genuinely reaches the whole company. */}
          {holiday.is_custom
            ? placeLabel(placeOf(holiday), countryNames, multiCountry)
            : regionLabel(holiday.region)}
        </span>
        {holiday.is_custom && (
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded whitespace-nowrap">
            Your day
          </span>
        )}
        {action}
      </div>
    </div>
  );

  return (
    <>
      <div className="divide-y divide-border">
        {holidays.map((holiday) => (
          <Row
            key={holiday.id}
            holiday={holiday}
            action={canEdit && (
              <Button
                variant={dense ? 'outline' : 'ghost'}
                size="sm"
                className={dense ? '' : 'text-destructive hover:text-destructive'}
                onClick={() => onRemove(holiday)}
                title={removeTitle(holiday)}
                data-testid={`delete-holiday-${holiday.id}`}
              >
                {dense ? 'Not for us' : <Trash />}
              </Button>
            )}
          />
        ))}
      </div>

      {/* ⚠️ THE OTHER HALF OF THE BIN ICON. Opt-outs exist so that a shared bank holiday
          survives one customer changing their mind — without somewhere to see and undo
          them, removing a holiday is a one-way door and the mechanism is pointless. */}
      {canEdit && optedOut.length > 0 && (
        <div className={dense ? 'mt-3 pt-3 border-t border-border' : 'mt-6'}>
          <p className={`${dense ? 'text-sm' : 'font-heading font-bold'} mb-1`}>
            Taken off your calendar ({optedOut.length})
          </p>
          <p className="text-xs text-muted-foreground mb-2">
            Your team is expected to work these. Put one back and it returns to everyone's
            calendar and their bank-holiday count.
          </p>
          <div className="divide-y divide-border">
            {optedOut.map((holiday) => (
              <Row
                key={holiday.id}
                holiday={holiday}
                muted
                action={(
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRestore(holiday)}
                    data-testid={`restore-holiday-${holiday.id}`}
                  >
                    Put back
                  </Button>
                )}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

/** The bin, kept local so the page and the wizard cannot end up with different icons. */
const Trash = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

export default HolidayDayList;
